import React, { useEffect, useMemo, useState } from "react";
import { Mail, Phone, RefreshCw, Search, Inbox, Building2, Calendar } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "In Progress", "Closed"];
const STATUS_STYLES = {
  New: "bg-blue-100 text-blue-800 ring-blue-200",
  "In Progress": "bg-amber-100 text-amber-800 ring-amber-200",
  Closed: "bg-emerald-100 text-emerald-800 ring-emerald-200",
};

function fmt(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
}

export default function AdmissionInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [instituteFilter, setInstituteFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (instituteFilter) params.institute_id = instituteFilter;
      const { data } = await api.get("/admin/admission-inquiries", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load inquiries");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, instituteFilter]);

  const institutes = useMemo(() => {
    const m = new Map();
    items.forEach((i) => { if (i.institute_id && i.institute_name) m.set(i.institute_id, i.institute_name); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      i.candidate_name?.toLowerCase().includes(q) ||
      i.mobile?.includes(q) ||
      (i.email || "").toLowerCase().includes(q) ||
      (i.department_name || "").toLowerCase().includes(q) ||
      (i.institute_name || "").toLowerCase().includes(q) ||
      (i.inquiry_details || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const statusCount = (s) => items.filter((i) => i.status === s).length;

  return (
    <div className="space-y-6" data-testid="admin-admission-inquiries">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Admission Inquiries</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">All inquiries submitted across every institute&apos;s public website.</div>
        </div>
        <button type="button" onClick={load} data-testid="adm-inq-reload" className="inline-flex items-center gap-2 border border-[#e5e0d8] bg-white hover:bg-[#f5f1e8] text-[#234333] text-sm px-3 py-2 rounded-sm">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={items.length} accent="navy" testid="adm-inq-stat-total" />
        <StatCard label="New" value={statusCount("New")} accent="blue" testid="adm-inq-stat-new" />
        <StatCard label="In Progress" value={statusCount("In Progress")} accent="amber" testid="adm-inq-stat-progress" />
        <StatCard label="Closed" value={statusCount("Closed")} accent="emerald" testid="adm-inq-stat-closed" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white rounded-sm border border-[#e5e0d8] px-3 py-1.5 flex-1 min-w-[220px]">
          <Search size={14} className="text-[#6b655b]" />
          <input
            data-testid="adm-inq-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, mobile, email, institute, course…"
            className="flex-1 text-sm bg-transparent focus:outline-none"
          />
        </div>
        <select data-testid="adm-inq-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm bg-white border border-[#e5e0d8] rounded-sm px-3 py-2">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="adm-inq-filter-institute" value={instituteFilter} onChange={(e) => setInstituteFilter(e.target.value)} className="text-sm bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 min-w-[220px]">
          <option value="">All Institutes</option>
          {institutes.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b] py-6">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-14 text-center">
          <Inbox size={40} strokeWidth={1.2} className="mx-auto text-[#bcb6aa]" />
          <div className="mt-3 text-sm text-[#6b655b]">No inquiries match the current filters.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f5f1e8] text-[10px] uppercase tracking-wider text-[#6b655b]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Candidate</th>
                <th className="text-left px-4 py-3 font-semibold">Institute</th>
                <th className="text-left px-4 py-3 font-semibold">Course</th>
                <th className="text-left px-4 py-3 font-semibold">Contact</th>
                <th className="text-left px-4 py-3 font-semibold">Details</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Received</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} data-testid={`adm-inq-row-${i.id}`} className="border-t border-[#e5e0d8] align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1c1a17]">{i.candidate_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-start gap-1.5 text-[#234333] font-medium">
                      <Building2 size={12} className="mt-0.5 shrink-0" />
                      <span>{i.institute_name || "—"}</span>
                    </div>
                    {i.institute_slug && <div className="text-[10px] text-[#6b655b] mt-1">/{i.institute_slug}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#5b5650]">{i.department_name || "—"}</td>
                  <td className="px-4 py-3">
                    <a href={`tel:${i.mobile}`} className="inline-flex items-center gap-1.5 text-[#234333] hover:text-[#d4a24c]"><Phone size={11} />{i.mobile}</a>
                    {i.email && <a href={`mailto:${i.email}`} className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-[#6b655b] hover:text-[#d4a24c] break-all"><Mail size={10} />{i.email}</a>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5b5650] max-w-sm">
                    <div className="line-clamp-3">{i.inquiry_details || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold ring-1 ${STATUS_STYLES[i.status] || STATUS_STYLES.New}`}>{i.status}</span>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#6b655b] whitespace-nowrap">
                    <div className="inline-flex items-center gap-1"><Calendar size={11} />{fmt(i.created_at)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  const cls = {
    navy: "bg-[#234333]/10 text-[#234333] border-[#234333]/30",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[accent] || "bg-white text-[#1c1a17] border-[#e5e0d8]";
  return (
    <div data-testid={testid} className={`border rounded-sm px-4 py-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-serif">{value}</div>
    </div>
  );
}
